import { Link } from 'react-router-dom'
import { CheckCircle2, FileText, GitBranchPlus, Lock, Pencil } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  formatSalesFollowupDate,
  SALES_FOLLOWUP_ACTIVITY_LABELS,
  SALES_FOLLOWUP_PRIORITY_CLASSES,
  type FollowupTreeNode,
} from '@/lib/sales-followups'
import { getQuotationQuickActionClass } from '@/lib/quotation-sales'
import { withServiceInstanceId } from '@/lib/service-instance'

type FollowupTreeProps = {
  nodes: FollowupTreeNode[]
  depth?: number
  serviceInstanceId: string | null
  completingId: number | null
  onComplete: (id: number) => void
  onEdit: (node: FollowupTreeNode) => void
  onAddChild: (node: FollowupTreeNode) => void
}

function buildQuotationCreatePath(serviceInstanceId: string | null, node: FollowupTreeNode) {
  const base = withServiceInstanceId('/services/quotations/new', serviceInstanceId)
  const [pathname, search = ''] = base.split('?')
  const params = new URLSearchParams(search)
  params.set('source_followup_id', String(node.id))
  if (node.enquiry_id != null) {
    params.set('enquiry_id', String(node.enquiry_id))
  }
  const query = params.toString()
  return query ? `${pathname}?${query}` : pathname
}

function FollowupTreeNodeCard({
  node,
  depth,
  serviceInstanceId,
  completingId,
  onComplete,
  onEdit,
  onAddChild,
}: {
  node: FollowupTreeNode
  depth: number
  serviceInstanceId: string | null
  completingId: number | null
  onComplete: (id: number) => void
  onEdit: (node: FollowupTreeNode) => void
  onAddChild: (node: FollowupTreeNode) => void
}) {
  const editable = node.is_editable !== false

  return (
    <div className="space-y-3" style={{ marginLeft: depth > 0 ? depth * 20 : 0 }}>
      <div className="rounded-xl border bg-background p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold">{node.title}</h3>
              <Badge variant="outline">{SALES_FOLLOWUP_ACTIVITY_LABELS[node.activity_type] ?? node.activity_type}</Badge>
              <Badge variant="outline" className={SALES_FOLLOWUP_PRIORITY_CLASSES[node.priority] ?? ''}>{node.priority}</Badge>
              <Badge variant={node.status === 'open' ? 'outline' : 'secondary'} className="capitalize">{node.status}</Badge>
              {!editable ? (
                <Badge variant="secondary" className="gap-1">
                  <Lock className="h-3 w-3" />
                  Locked
                </Badge>
              ) : null}
            </div>
            <div className="grid gap-2 text-sm text-muted-foreground md:grid-cols-2">
              <div><span className="font-medium text-foreground">Due:</span> {formatSalesFollowupDate(node.due_at)}</div>
              <div><span className="font-medium text-foreground">Assignee:</span> {node.assignee_name || '-'}</div>
              <div><span className="font-medium text-foreground">Created:</span> {formatSalesFollowupDate(node.created_at)}</div>
              <div><span className="font-medium text-foreground">Updated:</span> {formatSalesFollowupDate(node.updated_at)}</div>
            </div>
            {node.notes ? (
              <div className="rounded-lg border bg-muted/20 px-3 py-2 text-sm">
                <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Notes</div>
                <div className="whitespace-pre-wrap text-foreground">{node.notes}</div>
              </div>
            ) : null}
            {node.outcome_note ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm">
                <div className="mb-1 text-xs font-medium uppercase tracking-wide text-emerald-700">Outcome</div>
                <div className="whitespace-pre-wrap text-emerald-950">{node.outcome_note}</div>
              </div>
            ) : null}
            {(node.child_count ?? node.children.length) > 0 ? (
              <p className="text-xs text-muted-foreground">
                {(node.child_count ?? node.children.length)} child follow-up{(node.child_count ?? node.children.length) !== 1 ? 's' : ''}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {editable ? (
              <Button size="sm" variant="outline" onClick={() => onEdit(node)}>
                <Pencil className="mr-1 h-3.5 w-3.5" />
                Edit
              </Button>
            ) : null}
            <Button size="sm" variant="outline" className={getQuotationQuickActionClass('open')} onClick={() => onAddChild(node)}>
              <GitBranchPlus className="mr-1 h-3.5 w-3.5" />
              Add Child
            </Button>
            <Button size="sm" variant="outline" className={getQuotationQuickActionClass('open')} asChild>
              <Link to={buildQuotationCreatePath(serviceInstanceId, node)}>
                <FileText className="mr-1 h-3.5 w-3.5" />
                Quote
              </Link>
            </Button>
            {node.status === 'open' ? (
              <Button
                size="sm"
                variant="outline"
                className={getQuotationQuickActionClass('ready')}
                onClick={() => onComplete(node.id)}
                disabled={completingId === node.id}
              >
                <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                {completingId === node.id ? 'Completing...' : 'Complete'}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      {node.children.length > 0 ? (
        <div className="space-y-3 border-l border-dashed pl-4">
          {node.children.map((child) => (
            <FollowupTreeNodeCard
              key={child.id}
              node={child}
              depth={0}
              serviceInstanceId={serviceInstanceId}
              completingId={completingId}
              onComplete={onComplete}
              onEdit={onEdit}
              onAddChild={onAddChild}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

export function FollowupTree({
  nodes,
  depth = 0,
  serviceInstanceId,
  completingId,
  onComplete,
  onEdit,
  onAddChild,
}: FollowupTreeProps) {
  if (nodes.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">No follow-ups yet.</p>
  }

  return (
    <div className="space-y-4">
      {nodes.map((node) => (
        <FollowupTreeNodeCard
          key={node.id}
          node={node}
          depth={depth}
          serviceInstanceId={serviceInstanceId}
          completingId={completingId}
          onComplete={onComplete}
          onEdit={onEdit}
          onAddChild={onAddChild}
        />
      ))}
    </div>
  )
}
